import type { NavigationItem } from "@/components/ui/navigation/types";
import type { SerializedNavItem } from "./serialize";

/**
 * Merges saved customization (visibility + order, recursively into children)
 * onto the canonical default navigation tree.
 *
 * Rules:
 * - Icons / descriptions / hrefs always come from `defaults` (functions can't
 *   be serialized; the saved copy is authoritative only for `visible`/`order`).
 * - A saved `visible: false` hides the item; any other value keeps the default
 *   visibility (so newly-shipped items default to visible, never silently hidden).
 * - Items present in `defaults` but absent from `saved` are returned untouched
 *   (handles config that grew since the user last customized).
 */
export function mergeNavCustomization(
  defaults: NavigationItem[],
  saved: SerializedNavItem[]
): NavigationItem[] {
  return defaults.map((def) => {
    const match = saved.find((s) => s.id === def.id);
    if (!match) return def;

    return {
      ...def,
      visible: match.visible === false ? false : def.visible,
      order: match.order !== undefined ? match.order : def.order,
      children: def.children
        ? mergeNavCustomization(def.children, match.children ?? [])
        : def.children,
    };
  });
}
